// File: frontend/src/components/marketing/visuals/MarketingWalletVisual.js
//
// Marketing recreation of the PesaFii Wallet / Transactions pages: available
// balance, today's collections, a wallet-activity bar chart and a small
// transaction feed where one item transitions Processing → Completed. Fictional
// data; no API calls, no payment endpoints touched.

import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Stack, Divider, Button, useTheme, alpha } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { MarketingStatusBadge, MarketingMiniBars, SampleNote } from "./primitives";
import { useInViewOnce, useCountUp } from "../motion";
import { DEMO_WALLET, DEMO_TRANSACTIONS, fmtUGX } from "../../../data/marketingDemoData";

const MoBox = motion(Box);

export default function MarketingWalletVisual() {
  const theme = useTheme();
  const reduce = useReducedMotion();
  const [ref, inView] = useInViewOnce();
  const green = theme.palette.primary.main;
  const bal = useCountUp(DEMO_WALLET.available, { active: inView });

  // Flip the processing transaction to completed for the reconciliation story.
  const [completed, setCompleted] = useState(false);
  useEffect(() => {
    if (!inView || reduce) return;
    const t = setTimeout(() => setCompleted(true), 1600);
    return () => clearTimeout(t);
  }, [inView, reduce]);

  return (
    <Box ref={ref}>
      <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: alpha(green, 0.3), mb: 1.5,
        background: `radial-gradient(400px 160px at 0% 0%, ${alpha(green, theme.palette.mode === "dark" ? 0.14 : 0.1)}, transparent 70%), ${theme.palette.background.paper}` }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <AccountBalanceWalletIcon sx={{ fontSize: 17, color: green }} />
              <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 600 }}>Available balance</Typography>
            </Stack>
            <Typography sx={{ fontSize: 26, fontWeight: 800, color: "text.primary", lineHeight: 1.1 }}>
              UGX {Math.round(bal).toLocaleString()}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: "success.main", fontWeight: 700, mt: 0.5 }}>
              + {fmtUGX(DEMO_WALLET.collectionsToday)} collected today
            </Typography>
          </Box>
          <Button size="small" variant="outlined" sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, flexShrink: 0, borderColor: "divider", color: "text.primary", "&:hover": { borderColor: green } }}>
            Withdraw
          </Button>
        </Stack>
        <Box sx={{ mt: 1.5 }}>
          <MarketingMiniBars data={DEMO_WALLET.activity} active={inView} color={green} height={54} />
        </Box>
      </Paper>

      <Paper sx={{ p: 1.75, borderRadius: 2.5, border: "1px solid", borderColor: "divider" }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary", mb: 1 }}>Recent transactions</Typography>
        <Stack spacing={0.75} divider={<Divider sx={{ borderColor: "divider" }} />}>
          {DEMO_TRANSACTIONS.map((t, i) => {
            const status = i === 2 ? (completed ? "completed" : "processing") : t.status;
            return (
              <MoBox key={t.ref}
                initial={reduce ? false : { opacity: 0, x: 12 }}
                animate={inView ? { opacity: 1, x: 0 } : (reduce ? {} : { opacity: 0, x: 12 })}
                transition={{ duration: 0.4, delay: 0.1 + i * 0.1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: 12, fontWeight: 600, color: "text.primary" }}>{t.customer}</Typography>
                    <Typography sx={{ fontSize: 10, color: "text.disabled" }}>{t.ref}</Typography>
                  </Box>
                  <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary" }}>{fmtUGX(t.amount)}</Typography>
                    <MarketingStatusBadge status={status} />
                  </Stack>
                </Stack>
              </MoBox>
            );
          })}
        </Stack>
      </Paper>
      <SampleNote />
    </Box>
  );
}
